import type { VirtualItem, Virtualizer } from '@tanstack/react-virtual';
import React from 'react';

import { TableCell, TableRow } from '@ifs-gcp/components';
import type { Checklist, ChecklistRequirement, ValidationError } from '@ifs-gcp/core';

import type { BaseFilterableTableColumn } from '../../utils';
import { NumberCell } from './number-cell';
import { RequirementCell } from './requirement-cell';
import { ScoreCell } from './score-cell';

type RequirementRowProps = {
  visibleColumns: Record<string, BaseFilterableTableColumn>;
  row: ChecklistRequirement;
  domain: Checklist;
  error?: ValidationError[];
  language: string;
  activeColumnIds: string[];
  rowVirtualizer: Virtualizer<HTMLDivElement, Element>;
  virtualItem: VirtualItem;
  searchText: string;
};

export const RequirementRow = ({
  visibleColumns,
  row,
  domain,
  error,
  language,
  activeColumnIds,
  rowVirtualizer,
  virtualItem,
  searchText,
}: RequirementRowProps) => {
  const content = [
    {
      key: 'requirementNo',
      element: (
        <TableCell
          size="small"
          width={visibleColumns.requirementNo?.width}
          minWidth={visibleColumns.requirementNo?.minWidth}
        >
          <NumberCell row={row} error={error} search={searchText} language={language} />
        </TableCell>
      ),
    },
    {
      key: 'requirementText',
      element: (
        <TableCell
          size="small"
          width={visibleColumns.requirementText?.width}
          minWidth={visibleColumns.requirementText?.minWidth}
        >
          <RequirementCell row={row} search={searchText} language={language} />
        </TableCell>
      ),
    },
    {
      key: 'requirementScore',
      element: (
        <TableCell
          padding="none"
          width={visibleColumns.requirementScore?.width}
          minWidth={visibleColumns.requirementScore?.minWidth}
        >
          <ScoreCell row={row} domain={domain} />
        </TableCell>
      ),
    },
  ];

  const filteredColumns = content.filter(({ key }) => activeColumnIds.includes(key));

  return (
    <TableRow
      data-index={virtualItem.index}
      ref={rowVirtualizer.measureElement}
      error={Boolean(error?.length)}
      style={{
        transform: `translateY(${virtualItem.start}px)`,
      }}
    >
      {filteredColumns.map(({ key, element }) => (
        <React.Fragment key={key}>{element}</React.Fragment>
      ))}
    </TableRow>
  );
};
